import { useEffect, useMemo, useRef, useState } from 'react';

import SORT_OPTIONS from '@/shared/constants/centersSortOptions';
import useBowlingCentersStore from '@/shared/store/bowlingCenters';

import useSearchParamsManager from '../useSearchParamsManager';
import useBowlingCentersData from './useBowlingCentersData';
import useGeoLocation from './useGeoLocation';
import useRecentCentersData from './useRecentCentersData';

// Custom hook that holds the logic in the Home page
function useHome() {
  const { query, orderCriteria, updateSearchParams } = useSearchParamsManager({
    defaultOrderCriteria: {
      order: SORT_OPTIONS.GAMES,
      sort: 'desc',
    },
  });
  const { onRequestPermission, permissionDenied, location } = useGeoLocation();
  const { bowlingCenters } = useBowlingCentersData({
    location,
  });
  const { recents, updateRecents } = useBowlingCentersStore();
  const sortOptionsRef = useRef(null);

  // Get UUIDs from recent centers - memoize to prevent unnecessary re-renders
  const recentUuids = useMemo(
    () => recents.map((center) => center.uuid),
    [recents]
  );

  // Fetch recent centers data by UUIDs
  const { data: recentCentersData } = useRecentCentersData(recentUuids);

  const [searchTerm, setSearchTerm] = useState(query);
  // update onSearch in useBowlingCentersData
  // but after a certain time
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length > 3) {
        // scroll to sort options
        sortOptionsRef.current.scrollIntoView({ behavior: 'smooth' });

        const shouldChangeOrder =
          orderCriteria.order === SORT_OPTIONS.PROXIMITY &&
          !location &&
          searchTerm !== '';

        if (shouldChangeOrder) {
          return updateSearchParams({
            order: SORT_OPTIONS.GAMES,
            sort: 'asc',
            q: searchTerm,
          });
        }

        updateSearchParams({ q: searchTerm });
      } else if (searchTerm.length <= 3) {
        updateSearchParams({
          q: '',
          ...orderCriteria,
        });
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Update recent centers when fetched data is available
  useEffect(() => {
    if (!recentCentersData || recentCentersData.length === 0) return;

    // Create a map of fetched centers by UUID for quick lookup
    const fetchedCentersMap = new Map(
      recentCentersData.map((center) => [center.uuid, center])
    );

    // Update recents with fetched data, preserving order and keeping existing centers if not found
    const updatedRecents = recents
      .map((recentCenter) => {
        const fetchedCenter = fetchedCentersMap.get(recentCenter.uuid);
        return fetchedCenter || recentCenter;
      })
      .filter(Boolean); // Remove any null/undefined entries

    updateRecents(updatedRecents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentCentersData]);

  return {
    bowlingCenters,
    orderCriteria,
    searchTerm,
    onSort: updateSearchParams,
    onSearch: setSearchTerm,
    locationPermissionDenied: permissionDenied,
    onRequestPermission,
    showPermissionUi:
      orderCriteria.order === SORT_OPTIONS.PROXIMITY && !location,
    sortOptionsRef,
  };
}

export default useHome;
